<script>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import QuotesAPI from 'dashboard/api/quotes';
import ProductsAPI from 'dashboard/api/products';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import TeamsAPI from 'dashboard/api/teams';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import QuoteForm from './QuoteForm.vue';
import QuoteList from './QuoteList.vue';
export default {
  name: 'QuoteTab',
  components: {
    NextButton,
    Spinner,
    QuoteForm,
    QuoteList,
  },
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
    contact: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const { t } = useI18n();
    const store = useStore();
    const quotes = ref([]);
    const products = ref([]);
    const summary = ref({ total: 0, orcamento: 0, vendido: 0, total_vendido: 0 });
    const isLoading = ref(false);
    const selectedQuote = ref(null);
    const showForm = ref(false);
    const activeSection = ref('list'); // 'list', 'form', 'detail'
    const isFullscreen = ref(false);
    const isSplitView = ref(false);
    const quoteTabRef = ref(null);

    const toggleFullscreen = () => {
      isFullscreen.value = !isFullscreen.value;
      if (!isFullscreen.value) {
        isSplitView.value = false;
      }
    };

    const toggleSplitView = () => {
      isSplitView.value = !isSplitView.value;
    };

    // Get conversation from store
    const currentChat = computed(() => {
      return store.getters.getSelectedChat;
    });

    const allMessages = computed(() => {
      if (!currentChat.value?.id) return [];
      return currentChat.value.messages || [];
    });

    const contactName = computed(() => props.contact?.name || 'Cliente');
    const contactPhone = computed(() => props.contact?.phone_number || '');
    const contactId = computed(() => props.contact?.id);

    const fetchQuotes = async () => {
      isLoading.value = true;
      try {
        const response = await QuotesAPI.get({
          conversation_id: props.conversationId,
        });
        quotes.value = response.data.quotes || [];
        summary.value = response.data.summary || {};
      } catch (error) {
        useAlert(t('QUOTES.API.ERROR_LOADING'));
      } finally {
        isLoading.value = false;
      }
    };

    const fetchProducts = async () => {
      try {
        const response = await ProductsAPI.get({ active: true });
        products.value = response.data.products || [];
      } catch (error) {
        console.error('Error loading products:', error);
      }
    };

    const createQuote = async () => {
      const convId = Number(props.conversationId);
      const contId = contactId.value;

      if (!convId || !contId) {
        useAlert(t('QUOTES.API.ERROR_SAVING'));
        return;
      }

      try {
        const response = await QuotesAPI.create({
          quote: {
            conversation_id: convId,
            contact_id: contId,
          },
        });
        selectedQuote.value = response.data;
        activeSection.value = 'form';
        fetchQuotes();
        useAlert(t('QUOTES.API.CREATE_SUCCESS'));
      } catch (error) {
        useAlert(t('QUOTES.API.ERROR_SAVING'));
      }
    };

    const editQuote = async (quote) => {
      try {
        const response = await QuotesAPI.show(quote.id);
        selectedQuote.value = response.data;
        activeSection.value = 'form';
      } catch (error) {
        useAlert(t('QUOTES.API.ERROR_LOADING'));
      }
    };

    const viewQuote = async (quote) => {
      try {
        const response = await QuotesAPI.show(quote.id);
        selectedQuote.value = response.data;
        activeSection.value = 'detail';
      } catch (error) {
        useAlert(t('QUOTES.API.ERROR_LOADING'));
      }
    };

    const deleteQuote = async (quote) => {
      if (!confirm(t('QUOTES.DELETE.CONFIRM'))) return;
      try {
        await QuotesAPI.delete(quote.id);
        fetchQuotes();
        useAlert(t('QUOTES.API.DELETE_SUCCESS'));
      } catch (error) {
        useAlert(t('QUOTES.API.ERROR_DELETING'));
      }
    };

    const updateStatus = async (quote, status) => {
      try {
        if (status === 'vendido') {
          await QuotesAPI.markAsSold(quote.id);
        } else if (status === 'saiu_entregar') {
          await QuotesAPI.markAsOutForDelivery(quote.id);
        } else if (status === 'entregue') {
          await QuotesAPI.markAsDelivered(quote.id);
        } else if (status === 'cancelado') {
          await QuotesAPI.markAsCancelled(quote.id);
        }
        fetchQuotes();
        useAlert(t('QUOTES.API.UPDATE_SUCCESS'));
      } catch (error) {
        useAlert(t('QUOTES.API.ERROR_SAVING'));
      }
    };

    const separateQuote = async (quote, options) => {
      try {
        await QuotesAPI.markAsSeparated(quote.id, {
          deliveryType: options.deliveryType,
          deliveryPersonId: options.deliveryPersonId || null,
          deliveryPersonName: options.deliveryPersonName || null,
          deliveryPersonPhone: options.deliveryPersonPhone || null,
        });

        // Assign conversation to "Expedição" team
        try {
          const teamsResponse = await TeamsAPI.get();
          const teams = teamsResponse.data || [];
          const expedicaoTeam = teams.find(
            team => team.name.toLowerCase() === 'expedição' || team.name.toLowerCase() === 'expedicao'
          );

          if (expedicaoTeam && currentChat.value?.id) {
            await ConversationAPI.assignTeam({
              conversationId: currentChat.value.id,
              teamId: expedicaoTeam.id,
            });
          }
        } catch (teamError) {
          console.error('Error assigning team:', teamError);
          // Don't fail the whole operation if team assignment fails
        }

        fetchQuotes();

        if (options.deliveryType === 'pickup') {
          useAlert('Mercadoria separada para retirada!');
        } else {
          useAlert('Mercadoria separada e entregador notificado!');
        }
      } catch (error) {
        console.error('Error separating quote:', error);
        useAlert('Erro ao separar mercadoria');
      }
    };

    const resendDriverNotification = async (quote) => {
      try {
        await QuotesAPI.resendDriverNotification(quote.id);
        useAlert('Mensagem reenviada ao entregador!');
      } catch (error) {
        console.error('Error resending notification:', error);
        const errorMessage = error.response?.data?.error || 'Erro ao reenviar mensagem';
        useAlert(errorMessage);
      }
    };

    const closeForm = () => {
      selectedQuote.value = null;
      activeSection.value = 'list';
      fetchQuotes();
    };

    const onQuoteSaved = () => {
      fetchQuotes();
    };

    const formatCurrency = (value) => {
      return `R$ ${Number(value || 0).toFixed(2)}`;
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    const formatMessageTime = (dateString) => {
      if (!dateString) return '';
      return new Date(dateString).toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    const getStatusClass = (status) => {
      const classes = {
        orcamento: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
        vendido: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
        separado: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
        saiu_entregar: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
        entregue: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
        cancelado: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      };
      return classes[status] || classes.orcamento;
    };

    const getStatusLabel = (status) => {
      const labels = {
        orcamento: t('QUOTES.STATUS.BUDGET'),
        vendido: t('QUOTES.STATUS.SOLD'),
        separado: 'SEPARANDO',
        saiu_entregar: 'SAIU P/ ENTREGAR',
        entregue: t('QUOTES.STATUS.DELIVERED'),
        cancelado: t('QUOTES.STATUS.CANCELLED'),
      };
      return labels[status] || status;
    };

    const printQuote = async (quote) => {
      try {
        const response = await QuotesAPI.show(quote.id);
        const fullQuote = response.data;

        const printWindow = window.open('', '_blank', 'width=400,height=700');
        if (!printWindow) {
          useAlert(t('QUOTES.PRINT.POPUP_BLOCKED'));
          return;
        }

        const statusLabels = {
          orcamento: 'ORÇAMENTO',
          vendido: 'VENDIDO',
          separado: 'SEPARADO',
          entregue: 'ENTREGUE',
          cancelado: 'CANCELADO',
        };

        const formatDateShort = (dateString) => {
          if (!dateString) return '-';
          return new Date(dateString).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          });
        };

        const formatMoney = (value) => {
          return `R$ ${Number(value || 0).toFixed(2)}`;
        };

        let itemsHtml = '';
        (fullQuote.items || []).forEach(item => {
          itemsHtml += `
            <div class="item">
              <div class="item-name">${item.product_name}</div>
              ${item.product_description ? `<div class="item-desc">${item.product_description}</div>` : ''}
              <div class="item-details">
                <span>${item.quantity}x ${formatMoney(item.unit_price)}</span>
                <span class="item-total">${formatMoney(item.total)}</span>
              </div>
              ${item.discount > 0 ? `<div class="item-discount">Desc: -${formatMoney(item.discount)}</div>` : ''}
              ${item.observation ? `<div class="item-obs">${item.observation}</div>` : ''}
            </div>
          `;
        });

        const html = `
          <!DOCTYPE html>
          <html>
          <head>
            <meta charset="UTF-8">
            <title>Orçamento #${fullQuote.id}</title>
            <style>
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              body {
                font-family: 'Courier New', monospace;
                font-size: 12px;
                background: #f5f5f5;
                margin: 0;
                padding: 0;
              }
              .toolbar {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                background: #1e293b;
                padding: 12px 16px;
                display: flex;
                justify-content: space-between;
                align-items: center;
                z-index: 100;
                box-shadow: 0 2px 8px rgba(0,0,0,0.2);
              }
              .toolbar-title {
                color: white;
                font-size: 14px;
                font-weight: bold;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
              }
              .toolbar-actions {
                display: flex;
                gap: 8px;
              }
              .btn {
                padding: 8px 16px;
                border: none;
                border-radius: 6px;
                cursor: pointer;
                font-size: 13px;
                font-weight: 500;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                transition: all 0.2s;
              }
              .btn-print {
                background: #3b82f6;
                color: white;
              }
              .btn-print:hover {
                background: #2563eb;
              }
              .btn-close {
                background: #64748b;
                color: white;
              }
              .btn-close:hover {
                background: #475569;
              }
              .preview-container {
                padding-top: 70px;
                display: flex;
                justify-content: center;
                padding-bottom: 20px;
              }
              .receipt {
                width: 80mm;
                padding: 5mm;
                background: white;
                box-shadow: 0 2px 12px rgba(0,0,0,0.15);
              }
              .header {
                text-align: center;
                border-bottom: 1px dashed #000;
                padding-bottom: 8px;
                margin-bottom: 8px;
              }
              .title {
                font-size: 16px;
                font-weight: bold;
                margin-bottom: 4px;
              }
              .quote-number {
                font-size: 14px;
                font-weight: bold;
              }
              .status {
                font-size: 11px;
                padding: 2px 8px;
                border: 1px solid #000;
                display: inline-block;
                margin: 4px 0;
              }
              .date {
                font-size: 10px;
                color: #666;
              }
              .customer {
                border-bottom: 1px dashed #000;
                padding-bottom: 8px;
                margin-bottom: 8px;
              }
              .customer-name {
                font-weight: bold;
              }
              .customer-phone {
                font-size: 11px;
              }
              .items {
                border-bottom: 1px dashed #000;
                padding-bottom: 8px;
                margin-bottom: 8px;
              }
              .item {
                margin-bottom: 8px;
              }
              .item-name {
                font-weight: bold;
                font-size: 11px;
              }
              .item-desc {
                font-size: 10px;
                color: #444;
                margin: 2px 0;
              }
              .item-details {
                display: flex;
                justify-content: space-between;
                font-size: 11px;
              }
              .item-total {
                font-weight: bold;
              }
              .item-discount {
                font-size: 10px;
                color: #666;
              }
              .item-obs {
                font-size: 10px;
                font-style: italic;
                color: #666;
              }
              .totals {
                margin-bottom: 8px;
              }
              .total-row {
                display: flex;
                justify-content: space-between;
                font-size: 11px;
              }
              .total-row.grand-total {
                font-size: 14px;
                font-weight: bold;
                border-top: 1px solid #000;
                padding-top: 4px;
                margin-top: 4px;
              }
              .discount {
                color: #666;
              }
              .address {
                border-top: 1px dashed #000;
                padding-top: 8px;
                margin-top: 8px;
                font-size: 11px;
              }
              .address-label {
                font-weight: bold;
                font-size: 10px;
              }
              .observations {
                border-top: 1px dashed #000;
                padding-top: 8px;
                margin-top: 8px;
                font-size: 10px;
              }
              .observations-label {
                font-weight: bold;
              }
              .footer {
                text-align: center;
                border-top: 1px dashed #000;
                padding-top: 8px;
                margin-top: 8px;
                font-size: 10px;
              }
              @media print {
                body {
                  background: white;
                }
                .toolbar {
                  display: none !important;
                }
                .preview-container {
                  padding-top: 0;
                  padding-bottom: 0;
                }
                .receipt {
                  width: 80mm;
                  box-shadow: none;
                  padding: 0;
                }
              }
            </style>
          </head>
          <body>
            <div class="toolbar">
              <span class="toolbar-title">Visualização do Orçamento #${fullQuote.id}</span>
              <div class="toolbar-actions">
                <button class="btn btn-close" onclick="window.close()">Fechar</button>
                <button class="btn btn-print" onclick="window.print()">Imprimir</button>
              </div>
            </div>

            <div class="preview-container">
              <div class="receipt">
                <div class="header">
                  <div class="title">ORÇAMENTO</div>
                  <div class="quote-number">#${fullQuote.id}</div>
                  <div class="status">${statusLabels[fullQuote.status] || fullQuote.status}</div>
                  <div class="date">${formatDateShort(fullQuote.created_at)}</div>
                </div>

                <div class="customer">
                  <div class="customer-name">${fullQuote.contact_name || contactName.value}</div>
                  <div class="customer-phone">${fullQuote.contact_phone || contactPhone.value}</div>
                </div>

                <div class="items">
                  ${itemsHtml}
                </div>

                <div class="totals">
                  <div class="total-row">
                    <span>Subtotal:</span>
                    <span>${formatMoney(fullQuote.subtotal)}</span>
                  </div>
                  ${fullQuote.discount > 0 ? `
                    <div class="total-row discount">
                      <span>Desconto:</span>
                      <span>-${formatMoney(fullQuote.discount)}</span>
                    </div>
                  ` : ''}
                  <div class="total-row grand-total">
                    <span>TOTAL:</span>
                    <span>${formatMoney(fullQuote.total)}</span>
                  </div>
                </div>

                ${fullQuote.delivery_address ? `
                  <div class="address">
                    <div class="address-label">ENTREGA:</div>
                    <div>${fullQuote.delivery_address}</div>
                  </div>
                ` : ''}

                ${fullQuote.observations ? `
                  <div class="observations">
                    <div class="observations-label">OBS:</div>
                    <div>${fullQuote.observations}</div>
                  </div>
                ` : ''}

                <div class="footer">
                  <div>Obrigado pela preferência!</div>
                  <div>${formatDateShort(new Date().toISOString())}</div>
                </div>
              </div>
            </div>
          </body>
          </html>
        `;

        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.focus();
      } catch (error) {
        console.error('Error printing quote:', error);
        useAlert(t('QUOTES.PRINT.ERROR'));
      }
    };

    watch(() => props.conversationId, (newId) => {
      if (newId) {
        fetchQuotes();
      }
    });

    onMounted(() => {
      fetchQuotes();
      fetchProducts();
    });

    return {
      quotes,
      products,
      summary,
      isLoading,
      selectedQuote,
      showForm,
      activeSection,
      isFullscreen,
      isSplitView,
      quoteTabRef,
      toggleFullscreen,
      toggleSplitView,
      currentChat,
      allMessages,
      contactName,
      contactPhone,
      contactId,
      createQuote,
      editQuote,
      viewQuote,
      deleteQuote,
      updateStatus,
      separateQuote,
      resendDriverNotification,
      closeForm,
      onQuoteSaved,
      formatCurrency,
      formatDate,
      formatMessageTime,
      getStatusClass,
      getStatusLabel,
      printQuote,
      t,
    };
  },
};
</script>

<template>
  <div
    ref="quoteTabRef"
    class="quote-tab h-full flex bg-white dark:bg-slate-900"
    :class="{
      'quote-tab--fullscreen': isFullscreen,
      'quote-tab--split': isSplitView
    }"
  >
    <!-- Main Quote Panel -->
    <div class="quote-panel flex-1 flex flex-col min-w-0" :class="{ 'quote-panel--split': isSplitView }">
      <!-- Header -->
      <div class="p-4 border-b border-slate-200 dark:border-slate-700">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h2 class="text-lg font-semibold text-slate-800 dark:text-slate-100">
              {{ t('QUOTES.TITLE') }}
            </h2>
            <p class="text-sm text-slate-500">
              {{ contactName }} | {{ contactPhone }}
            </p>
          </div>
          <div class="flex items-center gap-2">
            <!-- Split View Button (only in fullscreen) -->
            <button
              v-if="isFullscreen"
              type="button"
              class="p-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              :class="{ 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400': isSplitView }"
              title="Dividir tela com mensagens"
              @click="toggleSplitView"
            >
              <span class="i-lucide-columns-2 size-5" />
            </button>
            <!-- Fullscreen Button -->
            <button
              type="button"
              class="p-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              :title="isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'"
              @click="toggleFullscreen"
            >
              <span v-if="isFullscreen" class="i-lucide-minimize-2 size-5" />
              <span v-else class="i-lucide-maximize-2 size-5" />
            </button>
            <NextButton
              v-if="activeSection === 'list'"
              :label="t('QUOTES.NEW')"
              icon="i-lucide-plus"
              @click="createQuote"
            />
            <NextButton
              v-else
              faded
              slate
              :label="t('QUOTES.BACK_TO_LIST')"
              icon="i-lucide-arrow-left"
              @click="closeForm"
            />
          </div>
        </div>

      <!-- Summary Cards -->
      <div v-if="activeSection === 'list'" class="grid grid-cols-4 gap-3">
        <div class="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 text-center">
          <div class="text-2xl font-bold text-slate-800 dark:text-slate-100">
            {{ summary.total }}
          </div>
          <div class="text-xs text-slate-500">{{ t('QUOTES.SUMMARY.TOTAL') }}</div>
        </div>
        <div class="bg-blue-50 dark:bg-blue-900 rounded-lg p-3 text-center">
          <div class="text-2xl font-bold text-blue-600 dark:text-blue-300">
            {{ summary.orcamento }}
          </div>
          <div class="text-xs text-blue-500">{{ t('QUOTES.SUMMARY.PENDING') }}</div>
        </div>
        <div class="bg-green-50 dark:bg-green-900 rounded-lg p-3 text-center">
          <div class="text-2xl font-bold text-green-600 dark:text-green-300">
            {{ summary.vendido }}
          </div>
          <div class="text-xs text-green-500">{{ t('QUOTES.SUMMARY.SOLD') }}</div>
        </div>
        <div class="bg-emerald-50 dark:bg-emerald-900 rounded-lg p-3 text-center">
          <div class="text-lg font-bold text-emerald-600 dark:text-emerald-300">
            R$ {{ Number(summary.total_vendido || 0).toFixed(2) }}
          </div>
          <div class="text-xs text-emerald-500">{{ t('QUOTES.SUMMARY.REVENUE') }}</div>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-auto p-4">
      <!-- Loading -->
      <div v-if="isLoading" class="flex justify-center items-center h-full">
        <Spinner />
      </div>

      <!-- List View -->
      <QuoteList
        v-else-if="activeSection === 'list'"
        :quotes="quotes"
        @edit="editQuote"
        @view="viewQuote"
        @delete="deleteQuote"
        @update-status="updateStatus"
        @separate="separateQuote"
        @print="printQuote"
        @resend-notification="resendDriverNotification"
      />

      <!-- Form View -->
      <QuoteForm
        v-else-if="activeSection === 'form' && selectedQuote"
        :quote="selectedQuote"
        :products="products"
        :conversation-id="conversationId"
        :contact-name="contactName"
        :contact-phone="contactPhone"
        @saved="onQuoteSaved"
        @close="closeForm"
        @message-sent="closeForm"
      />

      <!-- Detail View (Read-only) -->
      <div v-else-if="activeSection === 'detail' && selectedQuote" class="space-y-6">
        <!-- Quote Header -->
        <div class="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-semibold text-slate-800 dark:text-slate-100">
              {{ t('QUOTES.DETAIL.QUOTE_NUMBER') }} #{{ selectedQuote.id }}
            </h3>
            <span
              :class="getStatusClass(selectedQuote.status)"
              class="px-3 py-1 text-sm font-medium rounded-full uppercase"
            >
              {{ getStatusLabel(selectedQuote.status) }}
            </span>
          </div>
          <div class="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span class="text-slate-500">{{ t('QUOTES.DETAIL.CUSTOMER') }}:</span>
              <span class="ml-2 text-slate-800 dark:text-slate-200">{{ selectedQuote.contact_name || '-' }}</span>
            </div>
            <div>
              <span class="text-slate-500">{{ t('QUOTES.DETAIL.PHONE') }}:</span>
              <span class="ml-2 text-slate-800 dark:text-slate-200">{{ selectedQuote.contact_phone || '-' }}</span>
            </div>
            <div>
              <span class="text-slate-500">{{ t('QUOTES.DETAIL.CREATED_AT') }}:</span>
              <span class="ml-2 text-slate-800 dark:text-slate-200">{{ formatDate(selectedQuote.created_at) }}</span>
            </div>
            <div v-if="selectedQuote.sold_at">
              <span class="text-slate-500">{{ t('QUOTES.DETAIL.SOLD_AT') }}:</span>
              <span class="ml-2 text-slate-800 dark:text-slate-200">{{ formatDate(selectedQuote.sold_at) }}</span>
            </div>
          </div>
        </div>

        <!-- Items -->
        <div class="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
          <h4 class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
            {{ t('QUOTES.DETAIL.ITEMS') }}
          </h4>
          <div class="space-y-2">
            <div
              v-for="item in selectedQuote.items"
              :key="item.id"
              class="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-700 last:border-0"
            >
              <div class="flex-1">
                <p class="text-sm font-medium text-slate-800 dark:text-slate-200">
                  {{ item.product_name }}
                </p>
                <p class="text-xs text-slate-500">
                  {{ formatCurrency(item.unit_price) }} x {{ item.quantity }}
                  <span v-if="item.discount > 0" class="text-green-600">
                    (- {{ formatCurrency(item.discount) }})
                  </span>
                </p>
                <p v-if="item.observation" class="text-xs text-slate-400 italic">
                  {{ item.observation }}
                </p>
              </div>
              <span class="text-sm font-medium text-slate-800 dark:text-slate-200">
                {{ formatCurrency(item.total) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Totals -->
        <div class="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 space-y-2">
          <div class="flex justify-between text-sm">
            <span class="text-slate-600 dark:text-slate-400">{{ t('QUOTES.FORM.SUBTOTAL') }}</span>
            <span class="font-medium text-slate-800 dark:text-slate-200">{{ formatCurrency(selectedQuote.subtotal) }}</span>
          </div>
          <div v-if="selectedQuote.discount > 0" class="flex justify-between text-sm">
            <span class="text-slate-600 dark:text-slate-400">{{ t('QUOTES.FORM.DISCOUNT') }}</span>
            <span class="font-medium text-green-600">- {{ formatCurrency(selectedQuote.discount) }}</span>
          </div>
          <hr class="border-slate-200 dark:border-slate-700" />
          <div class="flex justify-between text-lg">
            <span class="font-medium text-slate-800 dark:text-slate-200">{{ t('QUOTES.FORM.TOTAL') }}</span>
            <span class="font-bold text-green-600 dark:text-green-400">{{ formatCurrency(selectedQuote.total) }}</span>
          </div>
        </div>

        <!-- Delivery Address -->
        <div v-if="selectedQuote.delivery_address" class="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
          <h4 class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            {{ t('QUOTES.FORM.DELIVERY_ADDRESS') }}
          </h4>
          <p class="text-sm text-slate-600 dark:text-slate-400">
            {{ selectedQuote.delivery_address }}
          </p>
        </div>

        <!-- Observations -->
        <div v-if="selectedQuote.observations" class="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
          <h4 class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            {{ t('QUOTES.FORM.OBSERVATIONS') }}
          </h4>
          <p class="text-sm text-slate-600 dark:text-slate-400">
            {{ selectedQuote.observations }}
          </p>
        </div>

        <!-- Actions -->
        <div class="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
          <NextButton
            v-if="selectedQuote.status === 'orcamento'"
            faded
            slate
            :label="t('QUOTES.ACTIONS.EDIT')"
            icon="i-lucide-pencil"
            @click="editQuote(selectedQuote)"
          />
          <NextButton
            v-if="selectedQuote.status === 'orcamento'"
            emerald
            :label="t('QUOTES.ACTIONS.MARK_SOLD')"
            icon="i-lucide-check-circle"
            @click="updateStatus(selectedQuote, 'vendido')"
          />
          <NextButton
            v-if="selectedQuote.status === 'separado'"
            orange
            label="Saiu p/ Entregar"
            icon="i-lucide-truck"
            @click="updateStatus(selectedQuote, 'saiu_entregar')"
          />
          <NextButton
            v-if="selectedQuote.status === 'saiu_entregar'"
            blue
            :label="t('QUOTES.ACTIONS.MARK_DELIVERED')"
            icon="i-lucide-check-circle"
            @click="updateStatus(selectedQuote, 'entregue')"
          />
        </div>
      </div>
    </div>
    </div>

    <!-- Messages Panel (Split View) -->
    <div
      v-if="isSplitView && isFullscreen"
      class="messages-panel w-1/2 border-l border-slate-200 dark:border-slate-700 flex flex-col bg-slate-50 dark:bg-slate-800"
    >
      <div class="p-3 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
        <div class="flex items-center justify-between">
          <h3 class="text-sm font-semibold text-slate-700 dark:text-slate-300">
            Mensagens - {{ contactName }}
          </h3>
          <button
            type="button"
            class="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
            title="Fechar painel"
            @click="toggleSplitView"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>
      </div>
      <div class="flex-1 overflow-auto p-3 space-y-2">
        <template v-if="allMessages.length > 0">
          <div
            v-for="message in allMessages"
            :key="message.id"
            class="message-item"
            :class="{
              'message-item--outgoing': message.message_type === 1,
              'message-item--incoming': message.message_type === 0
            }"
          >
            <div
              class="message-bubble"
              :class="{
                'bg-woot-500 text-white ml-auto': message.message_type === 1,
                'bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200': message.message_type === 0
              }"
            >
              <p class="text-sm whitespace-pre-wrap break-words">{{ message.content }}</p>
              <span class="text-xs opacity-70 mt-1 block">
                {{ formatMessageTime(message.created_at) }}
              </span>
            </div>
          </div>
        </template>
        <div v-else class="flex items-center justify-center h-full text-slate-400">
          <p>Nenhuma mensagem</p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.quote-tab {
  min-height: 400px;
}

.quote-tab--fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  min-height: 100vh;
  overflow: hidden;
}

.quote-tab--split {
  flex-direction: row;
}

.quote-panel {
  overflow: auto;
}

.quote-panel--split {
  width: 50%;
  flex: none;
}

.messages-panel {
  height: 100%;
  overflow: hidden;
}

.message-item {
  display: flex;
}

.message-item--outgoing {
  justify-content: flex-end;
}

.message-item--incoming {
  justify-content: flex-start;
}

.message-bubble {
  max-width: 80%;
  padding: 8px 12px;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.message-item--outgoing .message-bubble {
  border-bottom-right-radius: 4px;
}

.message-item--incoming .message-bubble {
  border-bottom-left-radius: 4px;
}
</style>
